import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  Chip,
  IconButton,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { glassStyle } from '../theme/luxuryTheme';

interface Column {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  format?: (value: any) => string;
}

interface LuxuryTableProps {
  columns: Column[];
  data: any[];
  title?: string;
  subtitle?: string;
  onRowClick?: (row: any) => void;
}

const LuxuryTable: React.FC<LuxuryTableProps> = ({
  columns,
  data,
  title,
  subtitle,
  onRowClick,
}) => {
  const theme = useTheme();

  const rowVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.5,
      },
    }),
  };

  return (
    <Box
      sx={{
        ...glassStyle,
        borderRadius: '20px',
        overflow: 'hidden',
      }}
    >
      {(title || subtitle) && (
        <Box sx={{ p: 3, borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
          {title && (
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sx={{
                    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                    backgroundColor: 'rgba(255, 255, 255, 0.03)',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    padding: '16px 24px',
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  padding: '16px 24px',
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <motion.tr
                key={index}
                custom={index}
                initial="hidden"
                animate="visible"
                variants={rowVariants}
                style={{
                  cursor: onRowClick ? 'pointer' : 'default',
                }}
                onClick={() => onRowClick && onRowClick(row)}
              >
                <TableRow
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.04)',
                      '& .MuiTableCell-root': {
                        backgroundColor: 'transparent',
                      },
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                      padding: '20px 24px',
                      transition: 'all 0.3s ease',
                    },
                  }}
                >
                  {columns.map((column) => {
                    const value = row[column.id];
                    const formattedValue = column.format ? column.format(value) : value;

                    // Special handling for percentage changes
                    if (column.id.includes('change') || column.id.includes('percent')) {
                      const isPositive = value >= 0;
                      return (
                        <TableCell key={column.id} align={column.align || 'left'}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isPositive ? (
                              <TrendingUpIcon
                                sx={{
                                  fontSize: 16,
                                  color: theme.palette.success.main,
                                }}
                              />
                            ) : (
                              <TrendingDownIcon
                                sx={{
                                  fontSize: 16,
                                  color: theme.palette.error.main,
                                }}
                              />
                            )}
                            <Typography
                              variant="body2"
                              sx={{
                                color: isPositive
                                  ? theme.palette.success.main
                                  : theme.palette.error.main,
                                fontWeight: 600,
                              }}
                            >
                              {formattedValue}
                            </Typography>
                          </Box>
                        </TableCell>
                      );
                    }

                    // Special handling for status
                    if (column.id === 'status') {
                      return (
                        <TableCell key={column.id} align={column.align || 'left'}>
                          <Chip
                            label={value}
                            size="small"
                            sx={{
                              backgroundColor:
                                value === 'Active'
                                  ? 'rgba(0, 200, 150, 0.2)'
                                  : 'rgba(255, 77, 79, 0.2)',
                              color: value === 'Active' ? '#52c41a' : '#ff5252',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>
                      );
                    }

                    // Default rendering
                    return (
                      <TableCell key={column.id} align={column.align || 'left'}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: column.id === 'name' ? 600 : 400,
                            fontFamily:
                              typeof value === 'number'
                                ? '"Space Grotesk", monospace'
                                : 'inherit',
                          }}
                        >
                          {formattedValue}
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      sx={{
                        color: 'text.secondary',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              </motion.tr>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LuxuryTable;